import type { User, Flight, Friendship, Message, GroupChat, GroupMember, GroupMessage, ConversationDeletion } from '../types';

interface CachedAppData {
  currentUser: User;
  flights: Flight[];
  allFlights: Flight[];
  friends: User[];
  friendships: Friendship[];
  pendingRequests: Friendship[];
  messages: Message[];
  unreadMessageCount: number;
  referralCount: number;
  groupChats: GroupChat[];
  groupMembers: GroupMember[];
  groupMessages: GroupMessage[];
  conversationDeletions: ConversationDeletion[];
  cachedAt: string;
}

const CACHE_PREFIX = 'rostfound_cache_';

export function saveAppData(userId: string, data: Omit<CachedAppData, 'cachedAt'>): void {
  try {
    const payload: CachedAppData = { ...data, cachedAt: new Date().toISOString() };
    localStorage.setItem(CACHE_PREFIX + userId, JSON.stringify(payload));
  } catch {
    // localStorage full or unavailable — silently skip
  }
}

export function loadAppData(userId: string): CachedAppData | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + userId);
    if (!raw) return null;
    return JSON.parse(raw) as CachedAppData;
  } catch {
    return null;
  }
}

export function clearAppData(userId: string): void {
  localStorage.removeItem(CACHE_PREFIX + userId);
}

// --- Offline friend request outbox ---

const OUTBOX_KEY = 'rostfound_friend_outbox';

interface QueuedFriendRequest {
  handle: string;
  queuedAt: string;
}

export function queueFriendRequest(handle: string): void {
  try {
    const existing = getFriendRequestOutbox();
    if (existing.some((r) => r.handle === handle)) return; // dedup
    existing.push({ handle, queuedAt: new Date().toISOString() });
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(existing));
  } catch { /* ignore */ }
}

export function getFriendRequestOutbox(): QueuedFriendRequest[] {
  try {
    const raw = localStorage.getItem(OUTBOX_KEY);
    return raw ? (JSON.parse(raw) as QueuedFriendRequest[]) : [];
  } catch {
    return [];
  }
}

export function clearFriendRequestOutbox(): void {
  localStorage.removeItem(OUTBOX_KEY);
}

export function formatCacheAge(cachedAt: string): string {
  const diffMs = Date.now() - new Date(cachedAt).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
